import type {
  DragUpdateOptions,
  DragUpdateResult,
} from "@project/emerge-types";
import type * as THREE from "three";
import { InteriorType } from "../../enum/InteriorType";
import type { CaseInteriorOptions } from "../../interfaces/AccessoryCompartmentOptions";
import type { FlightCaseSettings } from "../../interfaces/FlightCaseSettings";
import {
  dimensionFromCornerDelta,
  gridStepFromOptions,
} from "../../utils/dragGrid";
import {
  calculatePanelsAndVolumes,
  getToleranceWithFallback,
  getTrayMaterialWithFallback,
} from "../interior-helpers/InteriorHelpers";
import {
  buildBoxCornerConstraint,
  cornerAxisPosition,
  cornerAxisSign,
} from "./cornerGeometry";
import type {
  CaseCornerPosition,
  CornerDragConstraintResult,
  CornerDragHandler,
} from "./types";

const MIN_SIZE = 50;
const MAX_SIZE = 5000;

function buildDisplayValues(
  mode: "xz" | "y",
  newWidth: number,
  newHeight: number,
  newDepth: number,
) {
  return {
    primary: {
      label: mode === "xz" ? "W×D" : "H",
      value: mode === "xz" ? newWidth : newHeight,
      unit: mode === "xz" ? `×${newDepth}mm` : "mm",
    },
  };
}

function applyCaseContentDimensions(
  interior: CaseInteriorOptions,
  dimensions: { width: number; height: number; depth: number },
): CaseInteriorOptions | null {
  const { tree, contents } = interior;
  const updatedInterior: CaseInteriorOptions = { ...interior };

  if (tree?.children?.length) {
    const firstChild = tree.children[0];
    if (firstChild === undefined) return null;
    if ("item" in firstChild && firstChild.item) {
      updatedInterior.tree = {
        ...tree,
        children: [
          {
            ...firstChild,
            item: {
              ...firstChild.item,
              ...dimensions,
            },
          },
          ...tree.children.slice(1),
        ],
      };
    }
  }

  if (contents?.length) {
    const firstContent = contents[0];
    if (firstContent === undefined) return null;
    updatedInterior.contents = [
      {
        ...firstContent,
        ...dimensions,
      },
      ...contents.slice(1),
    ];
  }

  if (!updatedInterior.tree && !updatedInterior.contents?.length) {
    return null;
  }

  return updatedInterior;
}

/**
 * Drag handler for CASE interior type.
 * Only enabled for single-item interiors (1 volume, 0 panels).
 */
export const CaseCornerDragHandler: CornerDragHandler = {
  shouldEnable(settings: FlightCaseSettings, buildstepName: string): boolean {
    const interior = settings.interiorOptions;
    if (interior.type !== InteriorType.CASE) return false;

    const { tolerance, trayMaterial } = interior;
    const finalTrayMaterial = getTrayMaterialWithFallback(
      trayMaterial,
      buildstepName,
    );
    if (!finalTrayMaterial) return false;

    const finalTolerance = getToleranceWithFallback(tolerance, buildstepName);

    const result = calculatePanelsAndVolumes(
      interior,
      finalTrayMaterial,
      finalTolerance,
      buildstepName,
    );
    if (!result) return false;

    return result.volumes.length === 1 && result.panels.length === 0;
  },

  buildConstraint(
    caseCornerPosition: CaseCornerPosition,
    position: THREE.Vector3,
    innerDimensions: THREE.Vector3,
    settings: FlightCaseSettings,
  ): CornerDragConstraintResult | null {
    const isBottom = caseCornerPosition.endsWith("Bottom");

    const interior = settings.interiorOptions as CaseInteriorOptions;
    const tolerance = interior.tolerance ?? { x: 7, y: 2, z: 7 };

    let currentWidth = innerDimensions.x - 2 * tolerance.x;
    let currentHeight = innerDimensions.y - tolerance.y;
    let currentDepth = innerDimensions.z - 2 * tolerance.z;

    // Try to get actual dimensions from tree or contents
    const { tree, contents } = interior;
    if (tree?.children?.[0]) {
      const firstChild = tree.children[0];
      if ("item" in firstChild && firstChild.item) {
        const item = firstChild.item;
        currentWidth = item.width ?? currentWidth;
        currentHeight = item.height ?? currentHeight;
        currentDepth = item.depth ?? currentDepth;
      }
    } else if (contents?.[0]) {
      currentWidth = contents[0].width ?? currentWidth;
      currentHeight = contents[0].height ?? currentHeight;
      currentDepth = contents[0].depth ?? currentDepth;
    }

    return buildBoxCornerConstraint(
      caseCornerPosition,
      position,
      "case",
      { currentWidth, currentHeight, currentDepth },
      MIN_SIZE,
      MAX_SIZE,
    );
  },

  updateSettings(
    newPosition: THREE.Vector3,
    settingsContext: Record<string, unknown>,
    currentSettings: FlightCaseSettings,
    options?: DragUpdateOptions,
  ): DragUpdateResult<FlightCaseSettings> | null {
    const mode = settingsContext.mode as "xz" | "y";
    const currentWidth = settingsContext.currentWidth as number;
    const currentHeight = settingsContext.currentHeight as number;
    const currentDepth = settingsContext.currentDepth as number;
    const restX = settingsContext.restX as number | undefined;
    const restY = settingsContext.restY as number | undefined;
    const restZ = settingsContext.restZ as number | undefined;
    const gridStepMm = gridStepFromOptions(options);

    if (mode === "xz" && (restX === undefined || restZ === undefined)) {
      return null;
    }
    if (mode === "y" && restY === undefined) return null;

    const xSign = cornerAxisSign(restX ?? 1);
    const zSign = cornerAxisSign(restZ ?? 1);

    const newWidth =
      mode === "xz" && restX !== undefined
        ? dimensionFromCornerDelta(
            currentWidth,
            xSign * (newPosition.x - restX),
            MIN_SIZE,
            gridStepMm,
          )
        : currentWidth;
    const newDepth =
      mode === "xz" && restZ !== undefined
        ? dimensionFromCornerDelta(
            currentDepth,
            zSign * (newPosition.z - restZ),
            MIN_SIZE,
            gridStepMm,
          )
        : currentDepth;
    const newHeight =
      mode === "y" && restY !== undefined
        ? dimensionFromCornerDelta(
            currentHeight,
            newPosition.y - restY,
            MIN_SIZE,
            gridStepMm,
          )
        : currentHeight;

    const snappedPosition = newPosition.clone();
    if (mode === "xz" && restX !== undefined && restZ !== undefined) {
      snappedPosition.x = cornerAxisPosition(
        restX,
        xSign,
        currentWidth,
        newWidth,
      );
      snappedPosition.z = cornerAxisPosition(
        restZ,
        zSign,
        currentDepth,
        newDepth,
      );
    } else if (restY !== undefined) {
      snappedPosition.y = cornerAxisPosition(
        restY,
        1,
        currentHeight,
        newHeight,
      );
    }

    const interior = currentSettings.interiorOptions as CaseInteriorOptions;
    const displayValues = buildDisplayValues(
      mode,
      newWidth,
      newHeight,
      newDepth,
    );

    const updatedInterior = applyCaseContentDimensions(interior, {
      width: newWidth,
      height: newHeight,
      depth: newDepth,
    });
    if (!updatedInterior) return null;

    return {
      settingsUpdate: {
        interiorOptions: updatedInterior,
      },
      displayValues,
      snappedPosition: [
        snappedPosition.x,
        snappedPosition.y,
        snappedPosition.z,
      ],
    };
  },
};
